import { Component, Inject, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { BehaviorSubject, Observable } from 'rxjs'
import { filter, finalize, first, map, switchMap, takeUntil, tap } from 'rxjs/operators'

import { Device, DeviceInstallation, DeviceStatus } from '@shared/models/device.model'
import { Vehicle } from '@shared/models/vehicle.model'
import { VehiclesService } from '@app/modules/client/vehicles/vehicles.service'
import { DevicesService } from '@shared/modules/devices/devices.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { DestroyService } from '@core/services/destroy.service'

interface InstallUninstallData {
  device: Device
  install: boolean
  vehicle?: Vehicle
  vehicleSelect?: boolean
}

@Component({
  selector: 'lin-device-install-uninstall-dialog',
  templateUrl: './device-install-uninstall-dialog.component.html',
  styleUrls: ['./device-install-uninstall-dialog.component.scss'],
  providers: [DestroyService],
  standalone: false,
})
export class DeviceInstallUninstallDialogComponent implements OnInit {
  form: FormGroup
  isSubmitting = false
  vehicle: Vehicle
  device: Device
  install: boolean
  isClient: boolean
  vehicleSelect: boolean

  private regNo$ = new BehaviorSubject<string>(null)
  private imei$ = new BehaviorSubject<string>(null)

  constructor(
    private fb: FormBuilder,
    private vehicleService: VehiclesService,
    private devicesService: DevicesService,
    private authService: AuthService,
    private readonly destroyService: DestroyService,
    public dialogRef: MatDialogRef<DeviceInstallUninstallDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: InstallUninstallData,
  ) {}

  ngOnInit() {
    this.authService.currentUser$.pipe(first()).subscribe((user) => (this.isClient = user.isClient()))
    this.vehicle = this.data.vehicle || this.data.device?.deviceInstallation?.vehicle
    this.device = this.data.device
    this.install = this.data.install
    this.vehicleSelect = this.data.vehicleSelect
    this.createForm()
    !this.vehicleSelect && this.getVehicleByRegNo()
    this.getDeviceByImei()
    this.setFormValues()
  }

  createForm() {
    this.form = this.fb.group({
      imei: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      regNo: ['', Validators.required],
      isUnavailable: '',
      blockingMessage: '',
      odometer: null,
    })

    if (this.vehicle) {
      this.form.controls.regNo.disable()
      this.form.markAsDirty()
    }

    if (this.device) {
      this.form.controls.imei.disable()
    }
  }

  getVehicleByRegNo() {
    this.regNo$
      .pipe(
        filter(Boolean),
        tap(() => this.vehicle && (this.vehicle = null)),
        switchMap((regNo: string) => (this.install ? this.fetchVehicles(regNo) : this.uninstallDevice(regNo))),
        takeUntil(this.destroyService),
      )
      .subscribe()
  }

  getDeviceByImei() {
    this.imei$
      .pipe(
        filter(Boolean),
        tap(() => this.device && (this.device = null)),
        switchMap((imei: string) => (this.install ? this.fetchDevices(imei) : this.uninstallDevice(imei, true))),
        takeUntil(this.destroyService),
      )
      .subscribe()
  }

  fetchDevices(imei: string): Observable<Device> {
    return this.devicesService.getDevices(0, 100, null, { imei }, ['id', 'model', 'imei', 'phone', 'deviceInstallation', 'status']).pipe(
      filter((devices) => devices && devices.data.length === 1),
      map((devices) => devices.data[0]),
      filter((device) => device.imei === imei && device.status !== DeviceStatus.Unavailable),
      tap((device) => (this.device = device)),
    )
  }

  fetchVehicles(regNo: string): Observable<Vehicle> {
    return this.vehicleService.getVehicles(0, 1, null, { regNo }, ['id', 'model', 'team', 'device']).pipe(
      map((vehicles: { data: Vehicle[] }) => vehicles.data[0]),
      filter((vehicle: Vehicle) => vehicle && !vehicle.device && vehicle.regNo.toLowerCase() === regNo.toLowerCase()),
      tap((vehicle: Vehicle) => (this.vehicle = vehicle)),
    )
  }

  uninstallDevice(value: string, isDevice = false) {
    const formControls = this.form.controls
    return this.getDeviceInstallation(value, isDevice).pipe(
      tap((deviceInstallation: DeviceInstallation) => {
        this.device = deviceInstallation.device
        this.vehicle = deviceInstallation.vehicle
        isDevice && formControls.regNo.setValue(deviceInstallation.vehicle.regNo)
        !isDevice && formControls.imei.setValue(deviceInstallation.device.imei)
        !isDevice && value !== deviceInstallation.vehicle.regNo && formControls.regNo.setValue(deviceInstallation.vehicle.regNo)
      }),
    )
  }

  getDeviceInstallation(value: string, isDevice: boolean): Observable<DeviceInstallation | null> {
    return isDevice ? this.devicesService.getDeviceInstallation(value) : this.devicesService.getDeviceInstallation(null, value)
  }

  setFormValues() {
    const { device, vehicle } = this

    this.form.patchValue({
      imei: (device && device.imei) || '',
      regNo: (vehicle && vehicle.regNo) || '',
      isUnavailable: device && device.status === DeviceStatus.Unavailable,
      blockingMessage: device && device.blockingMessage,
      odometer: null,
    })

    !this.vehicleSelect &&
      this.form.controls.regNo.valueChanges
        .pipe(takeUntil(this.destroyService))
        .subscribe((regNo: string) => this.handleValueChanges(regNo))

    this.form.controls.imei.valueChanges
      .pipe(takeUntil(this.destroyService))
      .subscribe((imei: string) => this.handleValueChanges(imei, true))
  }

  handleValueChanges(value: string, isDevice = false) {
    const formControls = this.form.controls

    !value && this.resetValues(isDevice)

    const isNotValidDeviceControl = this.device && formControls.imei.value !== this.device.imei
    const isNotValidRegNoControl = this.vehicle && formControls.regNo.value !== this.vehicle.regNo
    const isNotValidFormControl = isDevice ? isNotValidDeviceControl : isNotValidRegNoControl

    if (this.install || (!this.device && !this.vehicle)) {
      isDevice ? this.imei$.next(value) : this.regNo$.next(value)
    }
    if (this.device && this.vehicle && isNotValidFormControl) {
      this.vehicle = null
      this.device = null

      isDevice ? formControls.regNo.reset() : formControls.imei.reset()
      isDevice ? this.imei$.next(value) : this.regNo$.next(value)
    }
  }

  private resetValues(isDevice: boolean): void {
    if (this.install) {
      if (isDevice) {
        this.device = null
        this.imei$.next(null)
      } else {
        this.regNo$.next(null)
        this.vehicle = null
      }
    } else {
      this.imei$.next(null)
      this.regNo$.next(null)

      if (this.vehicle || this.device) {
        const formControls = this.form.controls
        this.vehicle = null
        this.device = null

        if (isDevice) {
          formControls.regNo.reset()
        } else {
          formControls.imei.reset()
        }
      }
    }

    return
  }

  installOrUninstallDevice() {
    const saveData = this.prepareDataForSave()
    return this.install
      ? this.devicesService.installDevice(this.device.id, saveData)
      : this.devicesService.uninstallDevice(this.device.id, saveData)
  }

  prepareDataForSave() {
    const formData = this.form.value as { isUnavailable: boolean; blockingMessage: string; odometer: number }

    return {
      vehicleId: this.vehicle.id,
      status: formData.isUnavailable ? DeviceStatus.Unavailable : DeviceStatus.InStock,
      blockingMessage: formData.blockingMessage,
      odometer: formData.odometer * 1000,
    }
  }

  installOrUninstall() {
    this.isSubmitting = true

    this.installOrUninstallDevice()
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe((data: Device) => this.dialogRef.close(data))
  }

  displayVehicleRegNo(vehicle?: Vehicle): string | null {
    return vehicle?.regNo || null
  }

  getVehicleList = (regNo: string) => {
    if (regNo && typeof regNo === 'object') {
      this.vehicle = regNo
      return Promise.resolve()
    }

    this.vehicle = null

    return this.vehicleService
      .getVehicleList(0, 30, null, { clientId: this.device.team.clientId, regNo }, ['id', 'model', 'team', 'device'])
      .pipe(
        map((vehicles: Vehicle[]) => vehicles.filter((vehicle) => !vehicle.device)),
        takeUntil(this.destroyService),
      )
  }
}
